import { type LiveSummaryTag, TagChip } from "@storytell/ui/live-summary";
import { TbChevronDown, TbCopy, TbFolder, TbLink, TbSend, TbSettings, TbTag } from "solid-icons/tb";
import { type Component, Match, type ParentComponent, Show, Switch } from "solid-js";
import { BrandGlyph } from "~/components/marketing/BrandGlyph";
import { useScene, useTyping } from "~/hooks/useScene";
import { useI18n } from "~/i18n";
import { ScaledFrame } from "../ScaledFrame";
import { EndedBar, SessionWindow, Swap } from "./chief";
import { at, Cursor, MockScene } from "./parts";
import { SummaryPage } from "./summary";

export type HandoffAgent = {
  name: string;
  logo: { src: string; color: string; dark?: boolean };
  /** A terminal agent is launched with `command`; a paste agent gets a prompt to paste into its chat. */
  launch: { kind: "terminal"; command: string } | { kind: "paste" };
  plan: string;
};

export type HandoffSession = {
  project: string;
  session: string;
  duration: string;
  folder: string;
  tag: LiveSummaryTag;
  /** Where a terminal send lands: the context folder Chief writes into the project. */
  delivered: string;
  loaded: string;
  prompt: string;
};

const T = { cursor: 0.5, send: 1.6, sent: 1.9, typing: 2.2, read: 2.9, loaded: 3.4, plan: 4.1 };

export const AgentLogo: Component<{ logo: HandoffAgent["logo"]; class: string }> = (props) => (
  <span
    class="inline-flex text-(--brand)"
    classList={{ "dark:text-primary-text": props.logo.dark }}
    style={{ "--brand": props.logo.color }}
  >
    <BrandGlyph src={props.logo.src} class={props.class} />
  </span>
);

/**
 * One agent's handoff, played once: Send to AI agent on the ended Live
 * Session's Summary, then the agent picking the meeting up in its own window.
 */
export const HandoffScene: Component<{ agent: HandoffAgent; session: HandoffSession; label: string }> = (props) => {
  const { t } = useI18n();
  const scene = useScene();
  const command = () => (props.agent.launch.kind === "terminal" ? props.agent.launch.command : "");
  const typed = useTyping(command, scene, T.typing * 1000);
  const project = () => props.session.folder.split("/").pop();
  const contextFolder = () => props.session.delivered.split("/").pop();

  return (
    <MockScene scene={scene} label={props.label}>
      <ScaledFrame chrome="app" width={940} height={680} narrow={{ width: 400, height: 1080 }}>
        <div class="grid h-full @2xl:grid-cols-[minmax(0,1fr)_minmax(0,0.95fr)]">
          <div class="@container min-h-0 min-w-0">
            <SessionWindow
              project={props.session.project}
              title={props.session.session}
              facts={`${t("marketing.mock.summary.ended")} · ${props.session.duration}`}
              tab="summary"
              bar={<EndedBar class="mac-pill pointer-events-auto border border-primary-border bg-tile shadow-2xl" />}
            >
              <SummaryPage
                project={props.session.project}
                title={props.session.session}
                duration={props.session.duration}
              >
                <HandoffCard agent={props.agent.name} folder={props.session.folder} tag={props.session.tag}>
                  <Switch>
                    <Match when={props.agent.launch.kind === "terminal"}>
                      <span class="flex items-center gap-1.5 self-start rounded-md py-1 text-xs text-secondary-text">
                        <TbLink class="size-3.5 shrink-0" aria-hidden="true" />
                        {t("marketing.mock.handoff.connect", { agent: props.agent.name })}
                        <TbChevronDown class="size-3 shrink-0" aria-hidden="true" />
                      </span>
                    </Match>
                    <Match when={props.agent.launch.kind === "paste"}>
                      <p class="text-xs leading-relaxed text-muted-text">
                        {t("marketing.mock.handoff.clipboard", { agent: props.agent.name })}
                      </p>
                    </Match>
                  </Switch>
                  <span
                    class="press relative flex min-h-10 items-center justify-center gap-2 rounded-lg bg-brand px-3 text-sm font-medium text-white"
                    style={at(T.send)}
                  >
                    <TbSend class="size-4" aria-hidden="true" />
                    <Swap
                      swapAt={T.sent}
                      before={t("marketing.mock.handoff.send")}
                      after={t("marketing.mock.handoff.send_again")}
                    />
                    <Cursor start={T.cursor} />
                  </span>
                  <p class="card-appear text-xs leading-relaxed wrap-break-word text-muted-text" style={at(T.sent)}>
                    <Switch>
                      <Match when={props.agent.launch.kind === "terminal"}>
                        {t("marketing.mock.handoff.delivered")} · {props.session.delivered}
                      </Match>
                      <Match when={props.agent.launch.kind === "paste"}>
                        {t("marketing.mock.handoff.copied")} ·{" "}
                        {t("marketing.mock.handoff.paste_into", { agent: props.agent.name })}
                      </Match>
                    </Switch>
                  </p>
                </HandoffCard>
              </SummaryPage>
            </SessionWindow>
          </div>

          <div class="dark flex min-w-0 flex-col bg-inset text-primary-text">
            <p class="flex items-center gap-2 border-b border-primary-border px-4 py-2 font-mono text-[11px] text-muted-text">
              <AgentLogo logo={props.agent.logo} class="size-3.5" />
              <Switch>
                <Match when={props.agent.launch.kind === "terminal"}>
                  {project()} — {command()}
                </Match>
                <Match when={props.agent.launch.kind === "paste"}>{props.agent.name}</Match>
              </Switch>
            </p>
            <Switch>
              <Match when={props.agent.launch.kind === "terminal"}>
                <div class="flex flex-col gap-2 p-4 font-mono text-[12.5px] leading-relaxed text-secondary-text">
                  <p>
                    <span class="text-terminal-green">~/{project()}</span> <span class="text-muted-text">$</span>{" "}
                    <span class="text-primary-text">{typed()}</span>
                    <span class="ms-px inline-block h-3.5 w-1.5 translate-y-0.5 bg-secondary-text/70" />
                  </p>
                  <p class="msg-in" style={at(T.read)}>
                    <span class="text-(--brand)" style={{ "--brand": props.agent.logo.color }}>
                      ●
                    </span>{" "}
                    {t("marketing.mock.handoff.reading")} <span class="text-primary-text">{contextFolder()}/</span>
                  </p>
                  <p class="msg-in ps-4 text-muted-text" style={at(T.loaded)}>
                    ⎿ {props.session.loaded}
                  </p>
                  <p class="msg-in text-primary-text" style={at(T.plan)}>
                    <span class="text-(--brand)" style={{ "--brand": props.agent.logo.color }}>
                      ●
                    </span>{" "}
                    {props.agent.plan}
                  </p>
                </div>
              </Match>
              <Match when={props.agent.launch.kind === "paste"}>
                <div class="flex flex-1 flex-col gap-3 p-4 text-[13px] leading-snug">
                  <p class="msg-in ms-6 rounded-xl rounded-ee-sm bg-tile p-3 text-secondary-text" style={at(T.read)}>
                    <span class="mb-1.5 flex items-center gap-1.5 font-mono text-[10px] text-muted-text uppercase">
                      <TbCopy class="size-3" aria-hidden="true" />
                      {t("marketing.mock.handoff.pasted")}
                    </span>
                    {props.session.prompt}
                  </p>
                  <p class="msg-in flex gap-2 text-primary-text" style={at(T.plan)}>
                    <AgentLogo logo={props.agent.logo} class="mt-0.5 size-4" />
                    {props.agent.plan}
                  </p>
                </div>
              </Match>
            </Switch>
          </div>
        </div>
      </ScaledFrame>
    </MockScene>
  );
};

/**
 * The Summary's Send to AI agent card: which agent, the folder it runs in,
 * the tag the send is narrowed to, and what the caller puts under that.
 */
export const HandoffCard: ParentComponent<{ agent: string; folder: string; tag?: LiveSummaryTag }> = (props) => {
  const { t } = useI18n();
  return (
    <div class="flex flex-col gap-3 rounded-xl border border-primary-border bg-panel/50 p-4">
      <div class="flex items-start gap-2.5">
        <TbSend class="mt-0.5 size-4 shrink-0 text-secondary-text" aria-hidden="true" />
        <div class="min-w-0 flex-1">
          <h3 class="text-sm font-semibold text-primary-text">{t("marketing.mock.handoff.send")}</h3>
          <p class="mt-1 text-xs leading-relaxed text-muted-text">{t("marketing.mock.handoff.keep_working")}</p>
        </div>
      </div>
      <div class="flex min-w-0 items-center gap-2">
        <span class="flex min-w-0 flex-1 items-center rounded-lg border border-primary-border bg-panel p-2 text-sm text-primary-text">
          <span class="min-w-0 flex-1 truncate">{props.agent}</span>
          <TbChevronDown class="size-4 shrink-0 text-secondary-text" aria-hidden="true" />
        </span>
        <span class="rounded-md p-2 text-muted-text">
          <TbSettings class="size-4" aria-hidden="true" />
        </span>
      </div>
      <span class="flex min-w-0 items-center gap-2 self-start rounded-md p-1 text-xs text-secondary-text">
        <TbFolder class="size-3.5 shrink-0" aria-hidden="true" />
        <span class="min-w-0 truncate">{props.folder}</span>
      </span>
      <Show when={props.tag}>
        {(tag) => (
          <div class="flex min-w-0 flex-col gap-2">
            <span class="flex items-center gap-2 text-xs text-secondary-text">
              <TbTag class="size-3.5 shrink-0" aria-hidden="true" />
              {t("marketing.mock.handoff.only_tagged")}
            </span>
            <span class="flex flex-wrap items-center gap-1">
              <TagChip tag={tag()} selected />
            </span>
          </div>
        )}
      </Show>
      {props.children}
    </div>
  );
};
